import React, { useEffect, useMemo, useRef, useState } from "react";
import MicIcon from "@material-ui/icons/Mic";
import { resolveFeatures, withFeatureItems } from "Components/RichTextEditor";
import {
  buildShortcutMap,
  createShortcutExpansion,
  fetchOrderShortcuts,
} from "../../orderShortcuts";
import ShortcutsButton from "../OrderSheet/ShortcutsButton";
import { CaseNumberPage, CASE_NUMBER_PAGE_ITEMS } from "./caseNumberPage";
import useDictation from "./useDictation";

/**
 * The props that give a RichTextEditor the features a surface enables - see
 * Components/RichTextEditor/features.js for the list.
 *
 *   const { editorProps, dictation } = useEditorFeatures({
 *     features: { pageBreakButtons: true, saveShortcut: true, ... },
 *     preset: documentPreset,
 *     toolbar: NOTICE_TOOLBAR,
 *     caseNumber, disputeId,
 *     onSave: () => { if (!busy) handleSave(); },
 *     onPrint: handlePrint,
 *   });
 *   <RichTextEditor key={...} value={...} onChange={...} {...editorProps} />
 *
 * With every feature off, editorProps is exactly the preset and toolbar given:
 * nothing is added to the schema, the toolbar or the save path.
 *
 * Features are fixed at mount, like the extensions; remount the editor (and
 * the component calling this) with a new `key` to change them. caseNumber,
 * disputeId, onSave and onPrint may change freely - they are read when used.
 *
 * This is for notices and factsheets. The order sheet surfaces keep their own
 * composition (PostMatterLinks, the order page items, their dictation).
 */
export default function useEditorFeatures({
  features,
  preset,
  toolbar,
  caseNumber,
  disputeId,
  onSave,
  onPrint,
}) {
  const [enabled] = useState(() => resolveFeatures(features));
  const caseNumberRef = useRef(caseNumber);
  caseNumberRef.current = caseNumber;

  // Typing shortcuts: the Masters list, loaded once per mount, read through a
  // ref so the editor can mount before it arrives.
  const shortcutListRef = useRef([]);
  const shortcutMapRef = useRef({});
  useEffect(() => {
    if (!enabled.typingShortcuts) return undefined;
    let alive = true;
    fetchOrderShortcuts().then((list) => {
      if (!alive) return;
      shortcutListRef.current = list;
      shortcutMapRef.current = buildShortcutMap(list);
    });
    return () => {
      alive = false;
    };
  }, [enabled.typingShortcuts]);

  const dictation = useDictation({
    url:
      enabled.dictation && disputeId
        ? `/bac/arbitrator/cases/${disputeId}/transcribe/`
        : null,
  });

  // Once per mount, like RichTextEditor's own resolution of the preset.
  const composedPreset = useMemo(
    () => (options) => [
      ...preset(options),
      ...(enabled.typingShortcuts
        ? [createShortcutExpansion(() => shortcutMapRef.current)]
        : []),
      ...(enabled.pageBreakButtons
        ? [CaseNumberPage.configure({ getCaseNumber: () => caseNumberRef.current })]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const dictationState = dictation.state;
  const { toggle } = dictation;
  const customItems = useMemo(
    () => ({
      ...(enabled.pageBreakButtons ? CASE_NUMBER_PAGE_ITEMS : {}),
      ...(enabled.typingShortcuts
        ? {
            ordershortcuts: {
              title: "Typing shortcuts",
              render: () => <ShortcutsButton getList={() => shortcutListRef.current} />,
            },
          }
        : {}),
      ...(enabled.dictation
        ? {
            recordaudio: {
              title: "Record audio - click to start, click again to stop & transcribe",
              icon: <MicIcon fontSize="small" />,
              active: () => dictationState === "recording",
              disabled: () => dictationState === "transcribing",
              run: (editor) => toggle(editor),
            },
          }
        : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dictationState]
  );

  const featureToolbar = useMemo(() => withFeatureItems(toolbar, enabled), [toolbar, enabled]);

  return {
    editorProps: {
      preset: composedPreset,
      toolbar: featureToolbar,
      customItems,
      onSave: enabled.saveShortcut ? onSave : undefined,
      onPrint: enabled.printShortcut ? onPrint : undefined,
      features: { wordCount: enabled.wordCount },
    },
    dictation: { state: dictation.state, error: dictation.error, message: dictation.message },
  };
}
